import type {
  CrmActionConfig,
  CustomGraph,
  FindPeopleConfig,
  NotifyConfig,
  TriggerConfig,
} from '@/types/custom-playbooks';
import type { CustomAgent } from '@/types';
import {
  CONNECTION_TYPES,
  type AgentDto,
  type AgentsActivityDto,
  type ConnectionType,
  type SkillDto,
  type SkillKind,
} from '@/types/gtm-agents';

/**
 * How the libraries read: an age in Gumloop's short form, an agent's or a
 * play's activity off the activity payload, and the grid's column count.
 */

/**
 * An API timestamp as epoch milliseconds. Some fields come without a zone
 * (the server's naive UTC); those are read as UTC, not local time. NaN for
 * an unreadable one.
 */
export function apiTime(iso: string): number {
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) || !iso.includes('T');
  return new Date(zoned ? iso : iso + 'Z').getTime();
}

/** "now", "15m", "2h", "1d", "1mo", "2y". Blank for nothing or an unreadable date. */
export function compactAgo(
  iso: string | null | undefined,
  now: number = Date.now()
): string {
  if (!iso) return '';
  const then = apiTime(iso);
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor(Math.max(0, now - then) / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return minutes + 'm';
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h';
  const days = Math.floor(hours / 24);
  if (days < 30) return days + 'd';
  if (days < 365) return Math.floor(days / 30) + 'mo';
  return Math.floor(days / 365) + 'y';
}

export interface ActivitySummary {
  /** Runs over the payload's days. */
  runs: number;
  /** Per day, oldest first. */
  series: number[];
  lastRunAt: string | null;
}

/**
 * One agent's or play's activity. Null when there is no payload (the route
 * is missing or failed), so the caller shows nothing rather than a zero.
 */
export function activityOf(
  activity: AgentsActivityDto | undefined,
  kind: 'agents' | 'plays',
  id: string
): ActivitySummary | null {
  if (!activity) return null;
  const found = activity[kind]?.[id];
  const series =
    found?.runs ?? new Array<number>(activity.days?.length ?? 0).fill(0);
  return {
    runs: series.reduce((sum, value) => sum + (value || 0), 0),
    series,
    lastRunAt: found?.lastRunAt ?? null,
  };
}

export const CONNECTION_NAME: Record<ConnectionType, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  attio: 'Attio',
  slack: 'Slack',
  email: 'Email',
};

/** The fixed order connections are listed in, as the API orders them. */
export const CONNECTION_ORDER = CONNECTION_TYPES;

/** The connections the agent has on, in a fixed order. */
export function agentConnectionTypes(
  agent: Pick<AgentDto, 'connections'>
): ConnectionType[] {
  const on = new Set(
    agent.connections
      .filter((connection) => connection.enabled)
      .map((connection) => connection.type)
  );
  return CONNECTION_ORDER.filter((type) => on.has(type));
}

/** Three columns from 1100px of content width, two from 560px, else one. */
export function libraryColumns(width: number): 1 | 2 | 3 {
  if (width >= 1100) return 3;
  if (width >= 560) return 2;
  return 1;
}

/**
 * The sparkline's line: points from x 2 to 110, 0 on y 22 and the highest
 * day on y 6, joined by curves that leave and reach each point level.
 */
export function sparklinePath(series: readonly number[]): string {
  const values = series.length ? series : [0, 0];
  const max = Math.max(...values, 0);
  const step = values.length > 1 ? 108 / (values.length - 1) : 0;
  const point = (index: number): [number, number] => [
    2 + index * step,
    max > 0 ? 22 - (Math.max(0, values[index]) / max) * 16 : 22,
  ];
  const fixed = (n: number) => n.toFixed(2);
  const [x0, y0] = point(0);
  let d = `M ${fixed(x0)} ${fixed(y0)}`;
  if (values.length === 1) return d + ` L 110 ${fixed(y0)}`;
  for (let index = 1; index < values.length; index += 1) {
    const [xa, ya] = point(index - 1);
    const [xb, yb] = point(index);
    d += ` C ${fixed(xa + step / 3)} ${fixed(ya)} ${fixed(
      xb - step / 3
    )} ${fixed(yb)} ${fixed(xb)} ${fixed(yb)}`;
  }
  return d;
}

/**
 * The connections a play's graph uses, in the fixed order: the CRM its
 * trigger watches, the CRM a skip of leads already in the CRM checks, the
 * CRM its CRM steps write to, and Slack when it posts there.
 */
export function playConnectionTypes(
  graph: CustomGraph | undefined
): ConnectionType[] {
  const used = new Set<ConnectionType>();
  for (const node of graph?.nodes ?? []) {
    if (node.type === 'trigger') {
      const config = node.config as Partial<TriggerConfig>;
      if (
        (config.source === 'crm' || config.source === 'crm_duplicates') &&
        config.crm?.crmType
      )
        used.add(config.crm.crmType);
      if (config.jobChange?.crmType) used.add(config.jobChange.crmType);
      const skip = config.cleanlist?.skipInCrm;
      if (skip?.enabled && skip.crmType) used.add(skip.crmType);
    } else if (node.type === 'find_people') {
      const skip = (node.config as Partial<FindPeopleConfig>).skipInCrm;
      if (skip?.enabled && skip.crmType) used.add(skip.crmType);
    } else if (node.type === 'crm_action') {
      const crm = (node.config as Partial<CrmActionConfig>).crmType;
      if (crm) used.add(crm);
    } else if (node.type === 'notify') {
      if ((node.config as Partial<NotifyConfig>).channel === 'slack')
        used.add('slack');
    }
  }
  return CONNECTION_ORDER.filter((type) => used.has(type));
}

/** "2h ago", "Just now"; blank for nothing or an unreadable date. */
export function agoWords(
  iso: string | null | undefined,
  now: number = Date.now()
): string {
  const short = compactAgo(iso, now);
  if (!short) return '';
  return short === 'now' ? 'Just now' : short + ' ago';
}

/** The full date and time of an API timestamp, for a hover title. */
export function fullTime(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const time = apiTime(iso);
  return Number.isNaN(time) ? undefined : new Date(time).toLocaleString();
}

/** A skill's kind in words. */
export const SKILL_KIND_LABEL: Record<SkillKind, string> = {
  procedure: 'Procedure',
  record: 'Per record',
};

/** Which record skill a skill id names: built in, custom, or neither. */
export function recordSkillOf(
  id: string
):
  | { kind: 'builtin'; agentType: 'research' | 'qualification' }
  | { kind: 'custom'; customAgentId: string }
  | null {
  if (id === 'builtin:research')
    return { kind: 'builtin', agentType: 'research' };
  if (id === 'builtin:qualification')
    return { kind: 'builtin', agentType: 'qualification' };
  if (id.startsWith('custom:'))
    return { kind: 'custom', customAgentId: id.slice('custom:'.length) };
  return null;
}

/** The built in record skills an organization without agents keeps. */
const BUILTIN_RECORD_SKILLS = [
  {
    id: 'builtin:research',
    name: 'Research',
    description:
      "Deep-research a lead's company, contact, or both. Tuned to your ICP and value props.",
  },
  {
    id: 'builtin:qualification',
    name: 'Qualification',
    description:
      "Score the lead against your ICP and auto-check whether they're already in your CRM.",
  },
] as const;

/**
 * The Skills tab of an organization without agents, whose skills API is
 * fenced: built in Research and Qualification, then its custom record
 * skills, as the API would list them.
 */
export function recordOnlySkills(
  customAgents: readonly CustomAgent[]
): SkillDto[] {
  const row = (
    skill: Pick<SkillDto, 'id' | 'name' | 'description'> &
      Partial<Pick<SkillDto, 'createdById' | 'createdAt' | 'modifiedAt'>>
  ): SkillDto => ({
    kind: 'record',
    version: null,
    createdByKind: null,
    createdById: null,
    usedBy: [],
    createdAt: null,
    modifiedAt: null,
    ...skill,
  });
  return [
    ...BUILTIN_RECORD_SKILLS.map((skill) => row(skill)),
    ...customAgents.map((agent) =>
      row({
        id: 'custom:' + agent.id,
        name: agent.name,
        description: agent.description ?? null,
        createdById: agent.created_by_user_id,
        createdAt: agent.created_at,
        modifiedAt: agent.last_edited_at ?? agent.modified_at,
      })
    ),
  ];
}
